import React from "react";
import classNames from "classnames";
import { ButtonIcon } from "@shared/ui/button";
import { useWindowControls } from "../hooks/useWindowControls";
import { overlayTitleText } from "../overlayTitleText";
import "./MobileBottomSheetFrame.scss";
import { Icon } from "@src/shared/ui/icon";

/** Пропсы {@link MobileBottomSheetFrame}. */
export interface MobileBottomSheetFrameProps {
    /** Заголовок панели. */
    title?: React.ReactNode;
    /** Дополнительный текст под заголовком. */
    supportingTitle?: React.ReactNode;
    /** Основной контент bottom sheet. */
    children?: React.ReactNode;
    /** Footer со служебными действиями. */
    footer?: React.ReactNode;
    /** Дополнительный CSS-класс корневой панели. */
    className?: string;
    /** Дополнительный CSS-класс content-area. */
    contentClassName?: string;
    /** Дополнительный CSS-класс footer. */
    footerClassName?: string;
    /** Обработчик запроса закрытия. По умолчанию закрывает текущий overlay. */
    onRequestClose?: () => void;
    /**
     * Accessible label кнопки закрытия.
     * @default "Закрыть"
     */
    closeButtonLabel?: string;
}

/**
 * Modal bottom sheet shell поверх общего overlay stack. \
 * Отвечает только за layout панели; backdrop, focus trap, Escape/back и scroll lock живут в `OverlayProvider`.
 */
export const MobileBottomSheetFrame: React.FC<MobileBottomSheetFrameProps> = ({
    title,
    supportingTitle,
    children,
    footer,
    className,
    contentClassName,
    footerClassName,
    onRequestClose,
    closeButtonLabel = "Закрыть",
}) => {
    const windowControls = useWindowControls();
    const handleClose = onRequestClose ?? (() => windowControls.close());
    const titleText = overlayTitleText(title);
    const supportingText = overlayTitleText(supportingTitle);
    const hasHeader = title != null || supportingTitle != null;

    return (
        <section
            className={classNames("mobile_bottom_sheet_frame", className)}
            role="dialog"
            aria-modal={windowControls.blocking}
            aria-labelledby={hasHeader ? "mobile-bottom-sheet-title" : undefined}
            style={{ zIndex: windowControls.zIndex }}
        >
            <div className="mobile_bottom_sheet_frame__handle" aria-hidden="true" />
            <div className="mobile_bottom_sheet_frame__header">
                {hasHeader && (
                    <div className="mobile_bottom_sheet_frame__title_group">
                        <h2 id="mobile-bottom-sheet-title" className="mobile_bottom_sheet_frame__title">
                            {title != null && typeof title !== "string" && typeof title !== "number" ? title : titleText}
                        </h2>
                        {supportingTitle != null && (
                            <p className="mobile_bottom_sheet_frame__supporting_title">{supportingText}</p>
                        )}
                    </div>
                )}
                <ButtonIcon
                    className="mobile_bottom_sheet_frame__close"
                    size="sm"
                    variant="ghost"
                    aria-label={closeButtonLabel}
                    onClick={handleClose}
                >
                    <Icon src="/icons/common/xmark.svg" size="20px" />
                </ButtonIcon>
            </div>
            <div className={classNames("mobile_bottom_sheet_frame__content", "custom_scrollbar", contentClassName)}>
                {children}
            </div>
            {footer != null && (
                <div className={classNames("mobile_bottom_sheet_frame__footer", footerClassName)}>{footer}</div>
            )}
        </section>
    );
};
